import { userSchema, type User } from '@/schemas/auth.schema'

/**
 * Access-token persistence. The token is stored in `localStorage` under a
 * stable key so it survives reloads; the fetch client (`apiClient.ts`) reads it
 * to attach the `Authorization: Bearer` header, and the auth context writes it
 * on login and clears it on logout.
 *
 * The signed-in user is kept beside it, so a phone that cannot reach the
 * server when the app opens can still say who is signed in. Without it, a
 * technician who reopened the app with no signal was signed out, and could not
 * sign back in until they had signal again.
 */
const ACCESS_TOKEN_KEY = 'app.accessToken'
const USER_KEY = 'app.user'

export const tokenStorage = {
  /** The stored access token, or `null` when the user is signed out. */
  get(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  },

  /** Persist the access token after a successful login. */
  set(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token)
  },

  /** The user last confirmed by the server, or `null` if none is stored. */
  getUser(): User | null {
    const raw = localStorage.getItem(USER_KEY)
    if (raw === null) return null
    try {
      const parsed = userSchema.safeParse(JSON.parse(raw))
      return parsed.success ? parsed.data : null
    } catch {
      return null
    }
  },

  /** Remember the user the server just confirmed. */
  setUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  },

  /** Remove the access token and the user on logout or a 401. */
  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },
}
